import { useCallback } from 'react';
import { Close } from '@mui/icons-material';
import {
  Backdrop,
  Box,
  DialogProps,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';

import { SuccessDialogCheckIcon } from '~assets/icons';
import { LoadingButton } from '~components/atoms/custom';
import { useNavigateWithDelay } from '~hooks/useNavigateWithDelay';

const outerBoxStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '1024px',
  width: '100%',
};

const innerBoxStyle = {
  bgcolor: 'background.paper',
  boxShadow: 24,
  mx: 3,
  borderRadius: '8px',
  border: 'none',
  p: 2.5,
};

interface SuccessDialogProps {
  title?: string;
  subtitle?: string;
  open: boolean;
  onClose: () => void;

  onBtnClick: () => void;
  btnText?: string;
  isBtnLoading?: boolean;
  isBtnDisabled?: boolean;
}

export const SuccessDialog: React.FC<SuccessDialogProps> = ({
  title = 'Title',
  subtitle = 'Subtitle',
  open,
  onClose,

  onBtnClick,
  btnText = 'Submit',
  isBtnLoading,
  isBtnDisabled,
}) => {
  const handleCloseModal: DialogProps['onClose'] = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Box sx={outerBoxStyle}>
        <Box sx={innerBoxStyle}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 30,
              top: 8,
              color: theme.palette.grey[800],
            })}
          >
            <Close />
          </IconButton>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            width="100%"
            pb={5.5}
          >
            <SuccessDialogCheckIcon />

            {/* Title */}
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>

            {/* Subtitle */}
            <Typography color="text.placeholder" textAlign="center">
              {subtitle}
            </Typography>
          </Box>

          {/* Action Button */}
          <LoadingButton
            onClick={onBtnClick}
            disabled={isBtnDisabled}
            isLoading={isBtnLoading}
          >
            <Typography variant="body2" fontWeight={500}>
              {btnText}
            </Typography>
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

interface WithNavigationProps
  extends Omit<
    SuccessDialogProps,
    'onBtnClick' | 'isBtnLoading' | 'isBtnDisabled'
  > {
  path: string;
}

export const SuccessDialogWithNavigation: React.FC<WithNavigationProps> = ({
  path,
  ...props
}) => {
  const { navigate, isNavigating } = useNavigateWithDelay();
  const handleNavigate = useCallback(() => {
    navigate(path, {}, props.onClose);
  }, [navigate, path, props.onClose]);

  return (
    <SuccessDialog
      {...props}
      // BUTTON ACTION
      onBtnClick={handleNavigate}
      isBtnLoading={isNavigating}
    />
  );
};
